import { useEffect, useState } from "react";
import type { ApiKeyDto, ApiKeyIssue, ForteXApi, UserDto } from "@fortex/api";
import { Button, Chip, Dialog, Field, Input, Skeleton, Switch, errorMessage } from "@fortex/map";
import { useApp } from "../../app/context";
import type { AppLabels } from "../../i18n";
import type { FieldErrors } from "../shared/CrudPage";
import { UserRoleSwitches } from "../users/UserRoleSwitches";

export const defaultGraceMinutes = 60;
export const maxGraceMinutes = 7 * 24 * 60;

export type ApiKeyUserDraft = { user: UserDto | null; name: string; fullName: string; active: boolean };

export function apiKeyDraftOf(user: UserDto | null): ApiKeyUserDraft {
  return {
    user,
    name: user?.name ?? "",
    fullName: user?.fullName ?? "",
    active: user?.active ?? true
  };
}

export function validateApiKeyDraft(draft: ApiKeyUserDraft, labels: AppLabels): FieldErrors {
  const errors: FieldErrors = {};

  if (draft.name.trim().length === 0) errors.name = labels.apiKeyNameRequired;

  return errors;
}

export function graceMinutesOf(text: string): number | null {
  if (!/^\d+$/.test(text.trim())) return null;

  const minutes = Number(text.trim());

  return minutes <= maxGraceMinutes ? minutes : null;
}

export async function saveApiKeyUser(api: ForteXApi, draft: ApiKeyUserDraft, onIssued: (issue: ApiKeyIssue) => void): Promise<UserDto> {
  if (draft.user === null) {
    const issue = await api.createApiKeyUser({ name: draft.name.trim(), fullName: draft.fullName.trim() || null });

    onIssued(issue);

    return issue.user;
  }

  return api.updateUser(draft.user.id, { name: draft.name.trim(), email: null, active: draft.active, fullName: draft.fullName.trim() || null, phoneNumber: null, newPassword: null, confirmNewPassword: null });
}

export type ApiKeyFormProps = {
  api: ForteXApi;
  labels: AppLabels;
  draft: ApiKeyUserDraft;
  setDraft: (draft: ApiKeyUserDraft) => void;
  errors: FieldErrors;
  canEditRoles: boolean;
  readOnly: boolean;
  dateFormat: Intl.DateTimeFormat;
  onIssued: (issue: ApiKeyIssue) => void;
};

export function ApiKeyForm({ api, labels, draft, setDraft, errors, canEditRoles, readOnly, dateFormat, onIssued }: ApiKeyFormProps) {
  const { toast } = useApp();
  const user = draft.user;
  const [keys, setKeys] = useState<ApiKeyDto[] | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [rollOpen, setRollOpen] = useState(false);
  const [graceText, setGraceText] = useState(String(defaultGraceMinutes));
  const [rollBusy, setRollBusy] = useState(false);
  const [revoking, setRevoking] = useState<ApiKeyDto | null>(null);
  const [revokeBusy, setRevokeBusy] = useState(false);

  useEffect(() => {
    if (user === null) return;

    const controller = new AbortController();

    api
      .userApiKeys(user.id, controller.signal)
      .then(result => {
        if (!controller.signal.aborted) setKeys(result);
      })
      .catch(reason => {
        if (!controller.signal.aborted) toast("danger", errorMessage(reason, labels));
      });

    return () => controller.abort();
  }, [api, user, reloadToken, toast, labels]);

  const dateOf = (value: string | null) => (value === null ? "" : dateFormat.format(new Date(value)));

  const runRoll = async () => {
    if (user === null) return;

    const graceMinutes = graceMinutesOf(graceText);

    if (graceMinutes === null) {
      toast("danger", labels.apiKeyGraceInvalid.replace("{max}", String(maxGraceMinutes)));
      return;
    }

    setRollBusy(true);

    try {
      const issue = await api.rollUserApiKey(user.id, graceMinutes);

      setRollOpen(false);
      setGraceText(String(defaultGraceMinutes));
      setReloadToken(a => a + 1);
      onIssued(issue);
    } catch (reason) {
      toast("danger", errorMessage(reason, labels));
    } finally {
      setRollBusy(false);
    }
  };

  const runRevoke = async () => {
    if (user === null || revoking === null) return;

    setRevokeBusy(true);

    try {
      await api.revokeUserApiKey(user.id, revoking.id);
      toast("success", labels.apiKeyRevokedText);
      setRevoking(null);
      setReloadToken(a => a + 1);
    } catch (reason) {
      toast("danger", errorMessage(reason, labels));
    } finally {
      setRevokeBusy(false);
    }
  };

  return (
    <form className="fxapp-form" onSubmit={event => event.preventDefault()} noValidate data-fxmap="api-key-form">
      <section className="fxapp-form-section">
        <h3 className="fxapp-settings-title">{labels.sectionAccount}</h3>
        <div className="fxapp-form-row">
          <Field label={labels.apiKeyName} required error={errors.name ?? null}>
            <Input value={draft.name} readOnly={readOnly} onChange={event => setDraft({ ...draft, name: event.target.value })} data-fxmap="api-key-name" />
          </Field>
          <Field label={labels.fullName}>
            <Input value={draft.fullName} readOnly={readOnly} onChange={event => setDraft({ ...draft, fullName: event.target.value })} data-fxmap="api-key-full-name" />
          </Field>
        </div>
        {user !== null ? <Switch checked={draft.active} disabled={readOnly || !user.editable} onChange={active => setDraft({ ...draft, active })} label={labels.active} showLabel /> : <p className="fxmap-muted">{labels.apiKeyCreateHint}</p>}
      </section>
      {user !== null ? (
        <section className="fxapp-form-section">
          <h3 className="fxapp-settings-title">{labels.sectionApiKeys}</h3>
          <div className="fxapp-toolbar">
            <Button kind="ghost" size="sm" icon="redo" label={labels.apiKeyRoll} disabled={readOnly} onClick={() => setRollOpen(true)} data-fxmap="api-key-roll" />
          </div>
          {keys === null ? (
            <Skeleton lines={2} />
          ) : keys.length === 0 ? (
            <p className="fxmap-muted">{labels.apiKeyNone}</p>
          ) : (
            <ul className="fxapp-key-list" data-fxmap="api-key-list">
              {keys.map(key => (
                <li key={key.id} className="fxapp-key-row" data-fxmap="api-key-row">
                  <span className="fxapp-key-prefix">{key.prefix}…</span>
                  <Chip tone={key.valid ? "success" : "neutral"}>{key.valid ? labels.apiKeyValid : labels.apiKeyNotValid}</Chip>
                  <span className="fxapp-key-dates">
                    <span>{`${labels.apiKeyCreated}: ${dateOf(key.createdDate)}`}</span>
                    {key.expiresAt !== null ? <span>{`${labels.apiKeyExpires}: ${dateOf(key.expiresAt)}`}</span> : null}
                    {key.revokedDate !== null ? <span>{`${labels.apiKeyRevoked}: ${dateOf(key.revokedDate)}`}</span> : null}
                    <span>{`${labels.apiKeyLastUsed}: ${key.lastUsedDate === null ? labels.apiKeyNeverUsed : dateOf(key.lastUsedDate)}`}</span>
                  </span>
                  {key.valid && !readOnly ? <Button kind="ghost" size="sm" icon="trash" label={labels.apiKeyRevoke} onClick={() => setRevoking(key)} data-fxmap="api-key-revoke" /> : <span />}
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}
      {user !== null && canEditRoles ? (
        <section className="fxapp-form-section">
          <h3 className="fxapp-settings-title">{labels.sectionRoles}</h3>
          <UserRoleSwitches api={api} labels={labels} userId={user.id} readOnly={readOnly} />
        </section>
      ) : null}
      <Dialog open={rollOpen} title={labels.apiKeyRoll} text={labels.apiKeyRollText} confirmLabel={labels.apiKeyRoll} cancelLabel={labels.cancel} busy={rollBusy} onConfirm={() => void runRoll()} onCancel={() => setRollOpen(false)} dataFxmap="api-key-roll-dialog">
        <Field label={labels.apiKeyGraceMinutes} hint={labels.apiKeyGraceHint}>
          <Input inputMode="numeric" value={graceText} onChange={event => setGraceText(event.target.value)} data-fxmap="api-key-grace" />
        </Field>
      </Dialog>
      <Dialog open={revoking !== null} title={labels.apiKeyRevoke} text={revoking === null ? null : labels.apiKeyRevokeText.replace("{prefix}", revoking.prefix)} confirmLabel={labels.apiKeyRevoke} cancelLabel={labels.cancel} danger busy={revokeBusy} onConfirm={() => void runRevoke()} onCancel={() => setRevoking(null)} dataFxmap="api-key-revoke-dialog" />
    </form>
  );
}
